import {
  Box,
  Button,
  Stack,
  Checkbox,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DateRangePicker } from "rsuite";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import {
  GetDailyConfigAccounts,
  getDailyConfigCardTypes,
} from "../../../../../API/nimbleAPI";
import { enqueueSnackbar } from "notistack";
import { useRouteLoaderData } from "react-router-dom";
import ArrowDownIcon from "@rsuite/icons/ArrowDown";
import { GetReadOnlyInputProps } from "../../../../../HelperFunctions/HelperFunctions";
import ViewTransactionsRanges from "../../../../../HelperFunctions/ViewTransactionsRanges";

const UndoImport = ({
  profitCenter,
  undoBankAcc,
  selectedCorp,
  loading,
  setLoading,
  setUndoBankAcc,
  UserPref,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const { corporations } = useRouteLoaderData("reconciliation");
  const [cardTypes, setCardTypes] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const viewTransaction = ViewTransactionsRanges();

  const handleFilterChange = (field, value) => {
    if (field === "viewTransaction") {
      setFieldValue("undoInfo.viewTransaction", value);
      const match = viewTransaction.find((opt) => opt.date === value);
      if (match) {
        setFieldValue("undoInfo.dateRange", [
          match.actualValue.start,
          match.actualValue.end,
        ]);
      }
    }

    if (field === "dateRange") {
      if (value === null) {
        // Clear the date range and reset viewTransaction
        setFieldValue("undoInfo.dateRange", [null, null]);
        setFieldValue("undoInfo.viewTransaction", "");
      } else if (value && Array.isArray(value) && value.length === 2) {
        const [start, end] = value;
        const matched = viewTransaction.find((opt) => {
          const optStart = opt.actualValue.start;
          const optEnd = opt.actualValue.end;
          return (
            optStart?.getTime?.() === start?.getTime?.() &&
            optEnd?.getTime?.() === end?.getTime?.()
          );
        });
        setFieldValue(
          "undoInfo.viewTransaction",
          matched ? matched.date : "Range",
        );
        setFieldValue("undoInfo.dateRange", value);
      }
    }
  };

  const today = (() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setHours(23, 59, 59, 999);

    return [start, end];
  })();

  useEffect(() => {
    getCardsTypes();
    const todayOption = viewTransaction.find((opt) => opt.date === "Today");
    if (todayOption && !values.undoInfo.viewTransaction) {
      setFieldValue("undoInfo.viewTransaction", "Today");
      setFieldValue("undoInfo.dateRange", [
        todayOption.actualValue.start,
        todayOption.actualValue.end,
      ]);
    }
  }, []);

  useEffect(() => {
    if (values.undoInfo.undoSelectedProfitCenter) {
      getBanckAccounts();
    }
  }, [values.undoInfo.undoSelectedProfitCenter]);

  // Close date picker on scroll when it's open
  useEffect(() => {
    if (!isDatePickerOpen) return;

    const handleScroll = () => {
      setIsDatePickerOpen(false);
    };

    // Listen to scroll events on window and all scrollable containers
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isDatePickerOpen]);

  const getBanckAccounts = async () => {
    try {
      // if (values.undoSelectedProfitCenter === "select") {
      //     return;
      // };

      const payload = {
        CorporationID: selectedCorp,
        PCID:
          values.undoInfo.undoSelectedProfitCenter === "select"
            ? ""
            : values.undoInfo.undoSelectedProfitCenter,
        // AccountID: val.ID,
      };
      const getBankAccList = await GetDailyConfigAccounts(payload);
      setUndoBankAcc(getBankAccList);

      if (getBankAccList.length === 2) {
        if (values.undoInfo.undoSelectedBankAcc === "select") {
          setFieldValue(
            "undoInfo.undoSelectedBankAcc",
            getBankAccList[1].AccountID,
          );
          setFieldValue(
            "undoInfo.undoSelectedBankAccName",
            getBankAccList[1].AccountName,
          );
        }
      }
    } catch (error) {
      enqueueSnackbar("Error while getting Bank accounts", {
        variant: "warning",
      });
    } finally {
      // setLoading(false);
    }
  };

  const getCardsTypes = async () => {
    try {
      const cards = await getDailyConfigCardTypes();
      setCardTypes(cards);
    } catch (error) {
      enqueueSnackbar("Error while getting Card types", { variant: "error" });
    }
  };

  const cardOptions = [
    { label: "Include Open batch", value: "open" },
    { label: "Include Matched", value: "matched" },
    { label: "Include Chargebacks ", value: "chargebacks" },
  ];

  const CARD_TYPES = [
    { label: "Select", value: 0 },
    { label: "Shift4", value: 1 },
    { label: "Payment Insider", value: 2 },
  ];

  const toggleCardSelection = (value) => {
    const current = values.undoInfo.selectedCards || [];
    const updated = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    setFieldValue("undoInfo.selectedCards", updated);
  };

  const toggleOptionSelection = (value) => {
    const current = values.undoInfo.selectedOptions || [];
    let updated;
    if (current.includes(value)) {
      updated = current.filter((item) => item !== value);
    } else {
      updated = [...current, value];
    }

    if (updated.length === 0) {
      setFieldValue("undoInfo.selectedCards", []);
    }

    // Clear selected cards only if chargebacks is selected ALONE (without open or matched)
    const hasChargebacks = updated.includes("chargebacks");
    const hasOpen = updated.includes("open");
    const hasMatched = updated.includes("matched");
    const isChargebacksAlone = hasChargebacks && !hasOpen && !hasMatched;

    if (isChargebacksAlone) {
      setFieldValue("undoInfo.selectedCards", []);
    }

    setFieldValue("undoInfo.selectedOptions", updated);
  };

  const handleSelectAllCards = (e) => {
    if (e.target.checked) {
      setFieldValue(
        "undoInfo.selectedCards",
        cardTypes.map((c) => c.Name),
      );
    } else {
      setFieldValue("undoInfo.selectedCards", []);
    }
  };

  const isAllSelected =
    (values.undoInfo.selectedCards || []).length === cardTypes.length;

  // Card types are disabled only when chargebacks is selected ALONE (without open or matched)
  // Cards are enabled when: no options selected, OR chargebacks not selected, OR chargebacks + open/matched
  const selectedOptions = values.undoInfo.selectedOptions || [];
  const hasChargebacks = selectedOptions.includes("chargebacks");
  const hasOpen = selectedOptions.includes("open");
  const hasMatched = selectedOptions.includes("matched");
  const isChargebacksAlone = hasChargebacks && !hasOpen && !hasMatched;

  const isCardEnabled = selectedOptions.length > 0 && !isChargebacksAlone;

  const CustomOptions = ({
    type,
    width = "200px",
    selected,
    onClick,
    disabled,
  }) => {
    return (
      <Button
        size="small"
        variant="contained"
        endIcon={
          <CheckCircleIcon
            style={{
              color: disabled ? "#d7d7d7" : selected ? "#00ba00" : "#d7d7d7",
            }}
          />
        }
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 400,
          fontSize: "11px",
          border: selected ? "0.1px solid #f6ffe6" : "0.1px solid #d7d7d7",
          backgroundColor: disabled
            ? "#f0f0f0"
            : selected
              ? "#f6ffe6"
              : "#ffffff",
          color: disabled ? "#a0a0a0" : "#333333",
          borderRadius: "3px",
          textTransform: "none",
          width: width,
          padding: "2px 10px",
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          margin: "4px 0",
        }}
        onClick={disabled ? undefined : onClick}
        disableRipple
        disableElevation
      >
        <span style={{ flexGrow: 1, textAlign: "left", overflow: "hidden" }}>
          {type}
        </span>
      </Button>
    );
  };

  return (
    <Stack
      height="100%"
      width={"100%"}
      padding={2}
      alignItems="center"
      spacing={2}
    // overflow={"auto"}
    >
      <Stack width="100%" flexDirection="column" gap={4} alignItems="center">
        <TextField
          value={
            corporations.find((c) => c.ID === values.selectedCorp)?.[
            UserPref ? "LegalName" : "Name"
            ] || ""
          }
          label="Corporation"
          variant="filled"
          sx={{ minWidth: 410 }}
          InputProps={{ readOnly: true }}
        />

        {profitCenter.length > 1 && (
          <Autocomplete
            size="small"
            disableClearable
            options={profitCenter}
            value={
              profitCenter.find(
                (p) => p.ID === values.undoInfo.undoSelectedProfitCenter,
              ) || null
            }
            getOptionLabel={(option) => option.Name}
            isOptionEqualToValue={(option, value) => option.ID === value.ID}
            onChange={(e, newValue) => {
              setFieldValue(
                "undoInfo.undoSelectedProfitCenter",
                newValue?.ID || "",
              );
              setFieldValue("undoInfo.undoSelectedBankAcc", "select");
              setFieldValue("undoInfo.undoSelectedBankAccName", "");
            }}
            getOptionDisabled={(option) => option.ID === "select"}
            sx={{ minWidth: 410 }}
            renderInput={(params) => (
              <TextField {...params}
                label={
                  <span>
                    Profit Center <span style={{ color: "red", fontSize: "18px" }}>*</span>
                  </span>
                }
                variant="filled" />
            )}
          />
        )}

        <Autocomplete
          size="small"
          disableClearable
          options={CARD_TYPES}
          sx={{ minWidth: 410 }}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          value={
            CARD_TYPES.find(
              (x) => x.label === values.undoInfo.MerchantProvider,
            ) || CARD_TYPES[0]
          }
          onChange={(e, opt) =>
            setFieldValue("undoInfo.MerchantProvider", opt?.label)
          }
          renderInput={(params) => (
            <TextField {...params}
              label={
                <span>
                  Merchant Provider <span style={{ color: "red", fontSize: "18px" }}>*</span>
                </span>
              }
              variant="filled" />
          )}
        />

        <Autocomplete
          size="small"
          disableClearable
          options={undoBankAcc}
          value={
            undoBankAcc.find(
              (b) => b.AccountID === values.undoInfo.undoSelectedBankAcc,
            ) || null
          }
          getOptionLabel={(option) => option.AccountName}
          isOptionEqualToValue={(option, value) =>
            option.AccountID === value.AccountID
          }
          onChange={(e, newValue) => {
            setFieldValue(
              "undoInfo.undoSelectedBankAcc",
              newValue?.AccountID || "",
            );
            setFieldValue(
              "undoInfo.undoSelectedBankAccName",
              newValue?.AccountName || "",
            );
          }}
          getOptionDisabled={(option) => option.AccountID === "select"}
          sx={{ minWidth: 410 }}
          renderInput={(params) => (
            <TextField {...params}
              label={
                <span>
                  Bank Account <span style={{ color: "red", fontSize: "18px" }}>*</span>
                </span>
              }
              variant="filled" />
          )}
        />

        <Autocomplete
          options={viewTransaction.map((opt) => opt.date)}
          size="small"
          disableClearable
          value={values.undoInfo.viewTransaction || ""}
          onChange={(e, val) => handleFilterChange("viewTransaction", val)}
          sx={{ minWidth: 410 }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...GetReadOnlyInputProps(params)}
              label={
                <span>
                  View Transaction <span style={{ color: "red", fontSize: "18px" }}>*</span>
                </span>
              }
              variant="filled"
            />
          )}
        />
      </Stack>
      <Stack>
        <Typography
          style={{ fontSize: "10px", fontWeight: 400, color: "#7b7b7b", marginLeft: 10 }}
        >

          <span>
            From & To<span style={{ color: "red", fontSize: "13px" }}>*</span>
          </span>

        </Typography>
        <DateRangePicker
          value={values.undoInfo.dateRange || [null, null]}
          onChange={(val) => handleFilterChange("dateRange", val)}
          open={isDatePickerOpen}
          onOpen={() => setIsDatePickerOpen(true)}
          onClose={() => setIsDatePickerOpen(false)}
          menuStyle={{ zIndex: 1400 }}
          defaultValue={new Date("2017-12-12 09:15:30")}
          cleanable={false}
          // cleanable 
          format="MM/dd/yyyy"
          size="lg"
          style={{ width: 410 }}
          showHeader={false}
          placement="autoVerticalStart"
          // className="no-clear"
          ranges={[
            {
              label: "Today",
              value: today,
            },
            {
              label: "Clear",
              value: null,
            },
          ]}
          // caretAs={ArrowDownIcon }
          caretAs={() => <ArrowDownIcon style={{ fontSize: 20 }} />}
          container={() => document.body}
        />
      </Stack>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        alignItems={"center"}
        justifyContent={"center"}
      >
        {cardOptions.map((opt) => (
          <CustomOptions
            key={opt.value}
            type={opt.label}
            selected={(values.undoInfo.selectedOptions || []).includes(
              opt.value,
            )}
            onClick={() => toggleOptionSelection(opt.value)}
            disabled={false}
          />
        ))}
      </Stack>

      <Box
        width={"80%"}
        // minHeight={"45%"}
        border={"1px solid #d7d7d7"}
        display={"flex"}
        flexDirection={"column"}
        borderRadius={1}
        bgcolor={"#f2f2f2"}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            height: "10%",
            p: 2,
          }}
        >
          <Typography fontSize={"9px"}>Select card type for Undo</Typography>

          <Stack direction={"row"} alignItems="center">
            <Typography fontSize={"9px"}>Select all card types</Typography>

            <Checkbox
              size="small"
              color="default"
              sx={{ padding: "2px" }}
              checked={isCardEnabled && isAllSelected}
              onChange={handleSelectAllCards}
              disabled={!isCardEnabled}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 1,
            width: "100%",
            padding: 1,
            alignItems: "center",
          }}
        >
          {cardTypes.map((card) => (
            <Box key={card.CardID}>
              <CustomOptions
                type={card.Name}
                width={"150px"}
                selected={(values.undoInfo.selectedCards || []).includes(
                  card.Name,
                )}
                onClick={() => toggleCardSelection(card.Name)}
                disabled={!isCardEnabled}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default UndoImport;
